import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  FlipHorizontal2,
  Images,
  Loader2,
  RotateCw,
  ScanLine,
  Upload,
  X,
} from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { Modal } from "@/components/Modal";
import { uploadImage, imageUrl } from "@/features/imageUpload";
import {
  appendScanItems,
  createScanDraft,
  deleteScanDraft,
  deleteScanItem,
  getScanDraft,
  listOpenScanDrafts,
  startScanIdentify,
  swapAllScanItemImages,
  swapScanItemImages,
  type ScanDraftItem,
} from "@/features/scanSession";

export const Route = createFileRoute("/tools/scan")({
  component: ScanIntakePage,
});

const CONDITIONS = ["NM", "LP", "MP", "HP", "Damaged"];
const GAMES = ["Pokemon", "Magic"] as const;
type Game = (typeof GAMES)[number];
// Remember the last game across sessions. A whole batch sent under the wrong
// game gets the wrong vision prompt and comes back all-blank (TNK-217), so the
// choice must stick rather than silently reset to Pokémon on every visit.
const GAME_STORAGE_KEY = "tcg-web.scan.game";

/** Image UPLOADS run a few at a time so a big drop neither stalls the UI nor
 *  hammers the image endpoint. (Identification is no longer client-side — the
 *  server worker handles it after upload; TNK-218.) */
const CONCURRENCY = 3;

type Stage = "upload" | "identify" | "review";
type Mode = "front" | "frontback";
type UploadStatus = "pending" | "uploading" | "uploaded" | "error";

/** One side of a card. `file` is the local upload (absent when a session is
 *  resumed — then we only have the server `ref`); `preview` is an object URL
 *  (fresh) or an /api/images URL (resumed); `ref` is the /api/images id once
 *  uploaded. */
interface Side {
  file?: File;
  preview: string;
  ref?: string;
}

/** A card during UPLOAD (the only client-side stage now — identify runs on the
 *  server, TNK-218). In front-only mode `back` is undefined. The FRONT side is
 *  what carries the card's primary image; "swap" / "flip all" reassign which
 *  side is the front (a duplex scanner may have fed cards face-down). `itemId`
 *  is the server draft-item id once the card is persisted to the scan session. */
interface ScanCard {
  key: number;
  itemId?: string;
  front: Side;
  back?: Side;
  uploadStatus: UploadStatus;
  uploadError?: string;
  /** Per-card condition (defaults to the batch control), persisted to the item
   *  so the commit merge reads it: NM/LP stack into one row, MP/HP/Damaged stay
   *  per-copy. */
  condition: string;
}

const IMAGE_MIME = /^image\//;

/** Run async tasks `limit`-at-a-time. */
async function runPool(tasks: Array<() => Promise<void>>, limit: number) {
  let i = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, tasks.length) }, async () => {
      while (i < tasks.length) await tasks[i++]();
    }),
  );
}

/**
 * Batch image scan intake (TNK-194). A staged flow: (1) upload card photos —
 * front-only, or front+back from a duplex scanner — to the server; (2) identify
 * each card from its front via Claude vision + the set resolver. Review, bin
 * assignment, and commit happen on the shared import review screen this
 * navigates to after Identify (TNK-216). This is Chaos Intake with the input
 * swapped from a typed `set number` to uploaded images.
 */
function ScanIntakePage() {
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>("upload");
  // Default to front+back — the operator scans two-sided on a duplex feeder, so
  // that's the common case; front-only is the exception (TNK-194).
  const [mode, setMode] = useState<Mode>("frontback");
  const [game, setGame] = useState<Game>(() => {
    const saved = localStorage.getItem(GAME_STORAGE_KEY);
    return saved === "Magic" || saved === "Pokemon" ? saved : "Pokemon";
  });
  const [condition, setCondition] = useState("NM");
  const [cards, setCards] = useState<ScanCard[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Cards currently spun to show their back (view-only; doesn't change which
  // side is the front).
  const [flippedView, setFlippedView] = useState<Set<number>>(new Set());
  // TNK-217 — confirm bulk "Flip all" (one misclick inverts every card).
  const [confirmFlipAll, setConfirmFlipAll] = useState(false);
  // Server-side scan session (an import draft, kind='scan') this batch persists
  // to, so a reload can resume it and the sweeper can clean it if abandoned.
  const [draftId, setDraftId] = useState<string | null>(null);
  // An open session found on load, offered for resume before we touch state.
  const [resumeId, setResumeId] = useState<string | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const keyRef = useRef(0);

  // Latest cards, readable from async paths (persist) without a stale closure —
  // so they see the authoritative front/back orientation after a flip, not the
  // value captured at render. TNK-218.
  const cardsRef = useRef(cards);
  cardsRef.current = cards;

  // The destination container is chosen on the shared import review screen the
  // scan navigates to after Identify (TNK-216) — not here at upload.

  // Release local preview object URLs on unmount (only blob: previews — a
  // resumed session's previews are /api/images URLs, nothing to revoke).
  useEffect(() => {
    return () => {
      setCards((prev) => {
        prev.forEach((c) => {
          if (c.front.preview.startsWith("blob:"))
            URL.revokeObjectURL(c.front.preview);
          if (c.back?.preview.startsWith("blob:"))
            URL.revokeObjectURL(c.back.preview);
        });
        return prev;
      });
    };
  }, []);

  // Persist the game so it survives reloads/sessions (see GAME_STORAGE_KEY).
  useEffect(() => {
    localStorage.setItem(GAME_STORAGE_KEY, game);
  }, [game]);

  // Detect an open scan session on mount, offered for resume.
  useEffect(() => {
    let cancelled = false;
    listOpenScanDrafts()
      .then((drafts) => {
        if (cancelled) return;
        const open = drafts.find((d) => d.status !== "committed");
        if (open) setResumeId(open.id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Server-side identify progress (TNK-218). While in the identify stage, poll
  // the draft — the worker runs on the API, so the operator can leave this page
  // and the work continues. When every item is terminal the scan is ready → go
  // to the shared review screen.
  const [identifyProgress, setIdentifyProgress] = useState({
    done: 0,
    total: 0,
  });
  useEffect(() => {
    if (stage !== "identify" || !draftId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { draft, items } = await getScanDraft(draftId);
        if (cancelled) return;
        const done = items.filter(
          (i) =>
            i.enrichmentStatus === "done" || i.enrichmentStatus === "failed",
        ).length;
        setIdentifyProgress({ done, total: items.length });
        if (
          items.length > 0 &&
          (draft.status === "ready" || done === items.length)
        ) {
          cancelled = true;
          void navigate({
            to: "/tools/imports/$id/review",
            params: { id: draftId },
          });
        }
      } catch {
        /* transient poll error — keep trying */
      }
    };
    void poll();
    const t = setInterval(poll, 1800);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [stage, draftId, navigate]);

  const allUploaded =
    cards.length > 0 && cards.every((c) => c.uploadStatus === "uploaded");
  // Gate "Confirm & identify" on the draft being persisted too (draftId set),
  // not just uploads finishing. Clicking in the gap between the two would run
  // identify with no draft to save against and dead-end (nothing persisted, no
  // navigation to review). draftId lands within ms of the last upload.
  // Gate Confirm on every card being uploaded AND persisted to the server
  // (itemId set), so identify + finalize can never silently skip a card that
  // never reached the server (TNK-218).
  const uploadsReady =
    allUploaded && !!draftId && cards.every((c) => !!c.itemId);
  const uploading = cards.some((c) => c.uploadStatus === "uploading");

  function patchCard(key: number, patch: Partial<ScanCard>) {
    setCards((prev) =>
      prev.map((c) => (c.key === key ? { ...c, ...patch } : c)),
    );
  }

  // ── Stage 1: upload ────────────────────────────────────────────────────────
  function makeSide(file: File): Side {
    return { file, preview: URL.createObjectURL(file) };
  }

  /** Upload a card's image(s) to the server; returns the refs (null on fail). */
  async function uploadOne(
    card: ScanCard,
  ): Promise<{ key: number; frontRef: string; backRef?: string } | null> {
    if (!card.front.file) return null;
    patchCard(card.key, { uploadStatus: "uploading", uploadError: undefined });
    try {
      const frontRef = await uploadImage(card.front.file);
      let backRef: string | undefined;
      if (card.back?.file) backRef = await uploadImage(card.back.file);
      setCards((prev) =>
        prev.map((c) =>
          c.key === card.key
            ? {
                ...c,
                uploadStatus: "uploaded",
                front: { ...c.front, ref: frontRef },
                back: c.back ? { ...c.back, ref: backRef } : undefined,
              }
            : c,
        ),
      );
      return { key: card.key, frontRef, backRef };
    } catch {
      patchCard(card.key, {
        uploadStatus: "error",
        uploadError: "Upload failed — retry.",
      });
      return null;
    }
  }

  /** Persist freshly-uploaded cards to the scan session — create it on the
   *  first batch, append after — and stamp each card with its server item id
   *  (returned in the same order we sent them). */
  async function persistUploaded(
    results: Array<{ key: number; frontRef: string; backRef?: string }>,
  ) {
    if (results.length === 0) return;
    // Carry the selected game + condition so the server identify worker
    // (TNK-218) reads the right catalog (Pokémon vs Magic) and the commit
    // merge keeps the condition. No `name` yet → the item stays 'pending'.
    const items = results.map((r) => {
      // Read the card's CURRENT front/back — a flip during upload swaps these in
      // local state but not in the upload `results`, so trust the live card so
      // the item is created with the side the operator means to identify.
      const c = cardsRef.current.find((x) => x.key === r.key);
      return {
        imageFrontRef: c?.front.ref ?? r.frontRef,
        imageBackRef: c?.back?.ref ?? r.backRef,
        parsed: { game, condition: c?.condition ?? condition },
      };
    });
    try {
      let serverItems: ScanDraftItem[];
      if (!draftId) {
        const created = await createScanDraft(items);
        setDraftId(created.draft.id);
        serverItems = created.items;
      } else {
        serverItems = (await appendScanItems(draftId, items)).items;
      }
      results.forEach((r, i) => {
        const id = serverItems[i]?.id;
        if (id) patchCard(r.key, { itemId: id });
      });
    } catch {
      setToast("Couldn’t save the session — uploads won’t resume on reload.");
    }
  }

  async function addFiles(files: File[]) {
    const images = files.filter((f) => IMAGE_MIME.test(f.type));
    if (images.length === 0) {
      setToast("Drop image files (JPG / PNG / WebP).");
      return;
    }
    if (images.length < files.length) {
      setToast(`Skipped ${files.length - images.length} non-image file(s).`);
    }
    const newCards: ScanCard[] = [];
    if (mode === "frontback") {
      // Duplex order: pair consecutive files (front, back, front, back…).
      for (let i = 0; i < images.length; i += 2) {
        const front = makeSide(images[i]);
        const back = images[i + 1] ? makeSide(images[i + 1]) : undefined;
        newCards.push({
          key: keyRef.current++,
          front,
          back,
          uploadStatus: "pending",
          condition,
        });
      }
      if (images.length % 2 === 1) {
        setToast(
          "Odd number of images — the last card has no back. Add it or use Swap.",
        );
      }
    } else {
      for (const file of images) {
        newCards.push({
          key: keyRef.current++,
          front: makeSide(file),
          uploadStatus: "pending",
          condition,
        });
      }
    }
    setCards((prev) => [...prev, ...newCards]);
    const results: Array<{ key: number; frontRef: string; backRef?: string }> =
      [];
    await runPool(
      newCards.map((c) => async () => {
        const r = await uploadOne(c);
        if (r) results.push(r);
      }),
      CONCURRENCY,
    );
    await persistUploaded(results);
  }

  /** Swap which side is the front (recognition side) for one card. Persists
   *  to the session so a resume identifies the right side. */
  function swapCard(key: number) {
    const c = cards.find((x) => x.key === key);
    if (!c?.back) return;
    setCards((prev) =>
      prev.map((x) =>
        x.key === key && x.back ? { ...x, front: x.back, back: x.front } : x,
      ),
    );
    // Best-effort persist so a resume shows the flip. Identification reads the
    // authoritative orientation from the finalize step at Confirm, not this, so
    // a dropped call here can't misidentify (TNK-218).
    if (draftId && c.itemId)
      void swapScanItemImages(draftId, c.itemId).catch(() => {});
  }

  /** Swap front/back on every two-sided card (fixes a face-down scan run).
   *  Persists so a resume keeps the corrected orientation. */
  function flipAll() {
    setCards((prev) =>
      prev.map((c) => (c.back ? { ...c, front: c.back, back: c.front } : c)),
    );
    if (draftId) void swapAllScanItemImages(draftId).catch(() => {});
  }

  function toggleFlipView(key: number) {
    setFlippedView((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function removeCard(key: number) {
    const removed = cards.find((c) => c.key === key);
    if (removed) {
      if (removed.front.preview.startsWith("blob:"))
        URL.revokeObjectURL(removed.front.preview);
      if (removed.back?.preview.startsWith("blob:"))
        URL.revokeObjectURL(removed.back.preview);
    }
    setCards((prev) => prev.filter((c) => c.key !== key));
    if (draftId && removed?.itemId)
      void deleteScanItem(draftId, removed.itemId).catch(() => {});
  }

  // ── Stage 2: identify (server-side, TNK-218) ────────────────────────────────
  // Identification runs on the API now: kick the background worker and move to
  // the polling stage. The browser is free to leave — the worker keeps going
  // and the scan resumes "ready" when the operator returns.
  async function startIdentify() {
    if (!draftId) return;
    // The authoritative final state the operator sees (orientation + per-card
    // condition). The server applies it in one transaction BEFORE the worker
    // runs, so identification can't race a pending flip — and it hands back a
    // confirmation count. Confirm is gated on every card being persisted, so
    // each card has an itemId + front.ref here (TNK-218).
    const finalize = cards
      .filter((c) => c.itemId && c.front.ref)
      .map((c) => ({
        itemId: c.itemId!,
        imageFrontRef: c.front.ref!,
        imageBackRef: c.back?.ref ?? null,
        condition: c.condition,
      }));
    setStage("identify");
    try {
      await startScanIdentify(draftId, finalize);
    } catch {
      setToast("Couldn’t confirm the batch to the server — try again.");
      setStage("upload");
    }
  }

  // ── Resume / discard ────────────────────────────────────────────────────────
  async function resumeSession() {
    if (!resumeId) return;
    try {
      const { draft, items } = await getScanDraft(resumeId);
      const restored: ScanCard[] = items.map((it) => {
        const parsed = (it.parsed ?? {}) as Record<string, unknown>;
        return {
          key: keyRef.current++,
          itemId: it.id,
          front: {
            preview: imageUrl(it.imageFrontRef) ?? "",
            ref: it.imageFrontRef,
          },
          back: it.imageBackRef
            ? { preview: imageUrl(it.imageBackRef) ?? "", ref: it.imageBackRef }
            : undefined,
          uploadStatus: "uploaded",
          condition:
            typeof parsed.condition === "string" ? parsed.condition : condition,
        };
      });
      setCards(restored);
      setDraftId(draft.id);
      setResumeId(null);
      setMode(restored.some((c) => c.back) ? "frontback" : "front");
      // Reflect the batch's actual game in the toggle so appending more cards
      // to a resumed session doesn't silently flip it to the remembered default.
      const restoredGame = (items[0]?.parsed as Record<string, unknown>)?.game;
      if (restoredGame === "Magic" || restoredGame === "Pokemon")
        setGame(restoredGame);
      // Route by where identification is (TNK-218): all items terminal → the
      // shared review screen; the worker is still running → the poll stage;
      // otherwise (uploaded but identify not yet started) → upload to Confirm.
      const pending = items.filter(
        (i) => i.enrichmentStatus === "pending",
      ).length;
      const enriching = items.filter(
        (i) => i.enrichmentStatus === "enriching",
      ).length;
      if (items.length > 0 && pending === 0 && enriching === 0) {
        void navigate({
          to: "/tools/imports/$id/review",
          params: { id: draft.id },
        });
      } else if (draft.status === "enriching" || enriching > 0) {
        setStage("identify");
      } else {
        setStage("upload");
      }
    } catch {
      setToast("Couldn’t resume the session.");
      setResumeId(null);
    }
  }

  function resetSession() {
    cards.forEach((c) => {
      if (c.front.preview.startsWith("blob:"))
        URL.revokeObjectURL(c.front.preview);
      if (c.back?.preview.startsWith("blob:"))
        URL.revokeObjectURL(c.back.preview);
    });
    setCards([]);
    setDraftId(null);
    setResumeId(null);
    setStage("upload");
  }

  function discardSession() {
    const id = draftId ?? resumeId;
    if (id) void deleteScanDraft(id).catch(() => {});
    resetSession();
  }

  // Commit (bin pick + confirm) now happens on the shared import review screen
  // the scan flow navigates to after Identify (TNK-216).

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
        <div className="flex items-center gap-3">
          {draftId && (
            <button
              type="button"
              onClick={discardSession}
              className="text-xs text-red-600 hover:underline"
            >
              Discard session
            </button>
          )}
          <StageStepper stage={stage} />
        </div>
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Scan Intake</h1>
        <p className="text-sm text-muted-foreground">
          {stage === "upload" &&
            "Upload card photos — front only, or front + back from a duplex scanner. They upload as you drop them."}
          {stage === "identify" &&
            "Identifying each card from its front. Confident matches are ready; the rest get flagged."}
          {stage === "review" &&
            "Review the matches. Accept the confident ones, fix the flagged ones."}
        </p>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto py-4">
        {resumeId && cards.length === 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
            <span className="text-foreground">
              You have an unfinished scan session — resume where you left off?
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void resumeSession()}
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
              >
                Resume
              </button>
              <button
                type="button"
                onClick={discardSession}
                className="rounded-md border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted"
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {stage === "upload" && (
          <UploadStage
            mode={mode}
            setMode={setMode}
            game={game}
            setGame={setGame}
            condition={condition}
            setCondition={setCondition}
            cards={cards}
            dragOver={dragOver}
            setDragOver={setDragOver}
            fileRef={fileRef}
            addFiles={addFiles}
            uploading={uploading}
            allUploaded={uploadsReady}
            flippedView={flippedView}
            onToggleFlip={toggleFlipView}
            onSwap={swapCard}
            onFlipAll={() => setConfirmFlipAll(true)}
            onRemove={removeCard}
            onRetryUpload={(key) => {
              const c = cards.find((x) => x.key === key);
              if (c)
                void uploadOne(c).then((r) => {
                  if (r && !c.itemId) void persistUploaded([r]);
                });
            }}
            onConfirm={() => void startIdentify()}
          />
        )}

        {stage === "identify" && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="text-sm text-foreground">
              Identifying{" "}
              {identifyProgress.total
                ? `${identifyProgress.done}/${identifyProgress.total}`
                : "…"}
            </div>
            <div className="w-full max-w-xs">
              <ProgressBar
                value={identifyProgress.done}
                total={identifyProgress.total || cards.length}
              />
            </div>
            <div className="max-w-sm text-xs text-muted-foreground">
              Running on the server —{" "}
              <span className="font-medium">safe to leave this page</span>.
              Identification continues in the background (paced under the AI
              rate limit); when you come back to Scan Intake it'll be ready for
              review.
            </div>
          </div>
        )}

        {toast && (
          <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{toast}</span>
            <button
              type="button"
              onClick={() => setToast(null)}
              aria-label="Dismiss"
              className="ml-auto"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <Modal
        open={confirmFlipAll}
        onClose={() => setConfirmFlipAll(false)}
        ariaLabel="Confirm flip all"
        panelClassName="max-w-sm rounded-lg border border-border bg-card p-5"
      >
        <h2 className="text-sm font-semibold text-foreground">
          Flip front ↔ back on every card?
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">
          This swaps front and back on all {cards.filter((c) => c.back).length}{" "}
          two-sided cards (use this for a face-down scan run). Click Flip all
          again to undo.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmFlipAll(false)}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirmFlipAll(false);
              flipAll();
            }}
            className="rounded-md border border-border bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:opacity-90"
          >
            Flip all
          </button>
        </div>
      </Modal>
    </section>
  );
}

// ── Stepper ──────────────────────────────────────────────────────────────────

function StageStepper({ stage }: { stage: Stage }) {
  const steps: Array<{ id: Stage; label: string }> = [
    { id: "upload", label: "Upload" },
    { id: "identify", label: "Identify" },
    { id: "review", label: "Review" },
  ];
  const activeIdx = steps.findIndex((s) => s.id === stage);
  return (
    <div className="flex items-center gap-1 text-xs">
      {steps.map((s, i) => (
        <span key={s.id} className="flex items-center gap-1">
          <span
            className={
              i === activeIdx
                ? "font-semibold text-foreground"
                : i < activeIdx
                  ? "text-emerald-600"
                  : "text-muted-foreground"
            }
          >
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
        </span>
      ))}
    </div>
  );
}

/** Thin completion bar — fills value/total. */
function ProgressBar({ value, total }: { value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full bg-primary transition-all duration-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ── Stage 1: Upload ────────────────────────────────────────────────────────

function UploadStage(props: {
  mode: Mode;
  setMode: (m: Mode) => void;
  game: Game;
  setGame: (g: Game) => void;
  condition: string;
  setCondition: (c: string) => void;
  cards: ScanCard[];
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  fileRef: React.RefObject<HTMLInputElement | null>;
  addFiles: (files: File[]) => void;
  uploading: boolean;
  allUploaded: boolean;
  flippedView: Set<number>;
  onToggleFlip: (key: number) => void;
  onSwap: (key: number) => void;
  onFlipAll: () => void;
  onRemove: (key: number) => void;
  onRetryUpload: (key: number) => void;
  onConfirm: () => void;
}) {
  const {
    mode,
    setMode,
    game,
    setGame,
    condition,
    setCondition,
    cards,
    dragOver,
    setDragOver,
    fileRef,
    addFiles,
    allUploaded,
    flippedView,
    onToggleFlip,
    onSwap,
    onFlipAll,
    onRemove,
    onRetryUpload,
    onConfirm,
  } = props;

  const uploadedCount = cards.filter(
    (c) => c.uploadStatus === "uploaded",
  ).length;
  const failedCount = cards.filter((c) => c.uploadStatus === "error").length;

  return (
    <>
      {/* Game — the first, hardest-to-miss choice. A whole batch sent under the
          wrong game gets the wrong vision prompt and comes back all-blank
          (TNK-217), so this is a prominent segmented toggle, not a dropdown
          buried with the defaults, and it remembers the last pick. */}
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Game
        </span>
        <div className="inline-flex w-fit rounded-lg border border-border bg-card p-1">
          {GAMES.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGame(g)}
              aria-pressed={game === g}
              className={`rounded-md px-5 py-1.5 text-sm font-semibold transition-colors ${
                game === g
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {g === "Pokemon" ? "Pokémon" : g}
            </button>
          ))}
        </div>
      </div>

      {/* Mode toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <ModeButton
          active={mode === "front"}
          onClick={() => setMode("front")}
          icon={<ScanLine className="h-4 w-4" />}
          label="Front only"
        />
        <ModeButton
          active={mode === "frontback"}
          onClick={() => setMode("frontback")}
          icon={<Images className="h-4 w-4" />}
          label="Front + back"
        />
        {mode === "frontback" && (
          <span className="text-xs text-muted-foreground">
            Images pair in order (front, back, front, back…).
          </span>
        )}
      </div>

      {/* Controls — the destination container is chosen later, on the shared
          import review screen (TNK-216). Game lives in its own prominent toggle
          above; only the default condition sits here. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          Default condition
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground"
          >
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Dropzone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(Array.from(e.dataTransfer.files));
        }}
        onClick={() => fileRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-10 text-center transition-colors ${
          dragOver
            ? "border-primary bg-primary/5"
            : "border-border bg-card hover:bg-muted"
        }`}
      >
        <Upload className="h-6 w-6 text-muted-foreground" />
        <div className="text-sm font-medium text-foreground">
          Drop card images here, or click to choose
        </div>
        <div className="text-xs text-muted-foreground">
          {mode === "frontback"
            ? "Front, back, front, back… — they pair up automatically"
            : "JPG, PNG, or WebP · many at once"}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
      </div>

      {/* Flip-all (front+back only) */}
      {mode === "frontback" && cards.length > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {cards.length} card{cards.length === 1 ? "" : "s"} paired
          </span>
          <button
            type="button"
            onClick={onFlipAll}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted"
            title="Swap front/back on every card (scanned face-down?)"
          >
            <FlipHorizontal2 className="h-3.5 w-3.5" /> Flip all
          </button>
        </div>
      )}

      {/* Upload queue progress */}
      {cards.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Queue</span>
            <span>
              {uploadedCount} / {cards.length} uploaded
              {failedCount > 0 ? ` · ${failedCount} failed` : ""}
            </span>
          </div>
          <ProgressBar value={uploadedCount} total={cards.length} />
        </div>
      )}

      {/* Upload grid */}
      {cards.length === 0 ? (
        <div className="rounded-md border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
          Dropped cards appear here.
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] gap-2">
          {cards.map((c) => (
            <UploadTile
              key={c.key}
              card={c}
              flipped={flippedView.has(c.key)}
              onToggleFlip={() => onToggleFlip(c.key)}
              onSwap={() => onSwap(c.key)}
              onRemove={() => onRemove(c.key)}
              onRetryUpload={() => onRetryUpload(c.key)}
            />
          ))}
        </div>
      )}

      {/* Confirm gate */}
      <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-border bg-background/95 py-3 backdrop-blur">
        {!allUploaded && cards.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {failedCount > 0
              ? `${failedCount} upload${failedCount === 1 ? "" : "s"} failed — retry or remove`
              : "Waiting for uploads to finish…"}
          </span>
        )}
        <button
          type="button"
          disabled={!allUploaded}
          onClick={onConfirm}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40"
        >
          Confirm &amp; identify <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </>
  );
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "border-primary bg-primary/10 text-foreground"
          : "border-border bg-card text-muted-foreground hover:bg-muted"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

/** Upload-stage tile: front (or flipped-to-back) preview + upload status +
 *  swap/flip/remove. */
function UploadTile({
  card,
  flipped,
  onToggleFlip,
  onSwap,
  onRemove,
  onRetryUpload,
}: {
  card: ScanCard;
  flipped: boolean;
  onToggleFlip: () => void;
  onSwap: () => void;
  onRemove: () => void;
  onRetryUpload: () => void;
}) {
  return (
    <div className="group relative flex flex-col overflow-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove"
        className="absolute left-1 top-1 z-10 rounded-full bg-black/50 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <CardFlip
        front={card.front.preview}
        back={card.back?.preview}
        flipped={flipped && !!card.back}
      />

      {/* Status / actions */}
      <div className="flex items-center justify-between gap-1 p-1.5">
        {card.uploadStatus === "uploading" ? (
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Uploading
          </span>
        ) : card.uploadStatus === "uploaded" ? (
          <span className="flex items-center gap-1 text-[11px] text-emerald-600">
            <Check className="h-3 w-3" /> Uploaded
          </span>
        ) : card.uploadStatus === "error" ? (
          <button
            type="button"
            onClick={onRetryUpload}
            className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:underline"
          >
            <RotateCw className="h-3 w-3" /> Retry
          </button>
        ) : (
          <span className="text-[11px] text-muted-foreground">Queued</span>
        )}

        {card.back && (
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={onToggleFlip}
              title={flipped ? "Show front" : "Show back"}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <RotateCw className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onSwap}
              title="Swap front/back"
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <FlipHorizontal2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** CSS 3D flip: both faces mounted with backface hidden; the back is pre-rotated
 *  so it lands upright at 180°. Mirrors the shop CardDetailModal flip. */
function CardFlip({
  front,
  back,
  flipped,
}: {
  front: string;
  back?: string;
  flipped: boolean;
}) {
  return (
    <div
      className="relative aspect-[3/4] bg-muted"
      style={{ perspective: "1000px" }}
    >
      <div
        className="relative h-full w-full transition-transform duration-500"
        style={{
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "none",
        }}
      >
        <img
          src={front}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{ backfaceVisibility: "hidden" }}
        />
        {back && (
          <img
            src={back}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          />
        )}
      </div>
    </div>
  );
}
